"use client"

import * as React from "react"
import { Maximize2, Monitor, Sparkles, X, Check } from "lucide-react"
import { Button } from "@/components/ui/button"

import { UserProfile } from "@/types/workspace"

interface FullscreenPromptModalProps {
  isOpen: boolean
  onClose: () => void
  onEnableFullscreen: () => void
  user?: UserProfile | null
  onUpdateFullSizeScreen?: (value: boolean) => void
}

export function FullscreenPromptModal({
  isOpen,
  onClose,
  onEnableFullscreen,
  user,
  onUpdateFullSizeScreen,
}: FullscreenPromptModalProps) {
  const [dontShowAgain, setDontShowAgain] = React.useState(false)

  if (!isOpen) return null

  const persistPreference = () => {
    if (dontShowAgain) {
      try {
        localStorage.setItem("bold_fullscreen_dismissed_permanent", "true")
        if (user?.id) {
          localStorage.setItem(`fullSizeScreen:${user.id}`, "true")
        }
      } catch (e) {}

      onUpdateFullSizeScreen?.(true)

      const token = typeof document !== "undefined"
        ? document.cookie
            .split("; ")
            .find((r) => r.startsWith("token="))
            ?.split("=")[1]
        : undefined

      if (token && user) {
        fetch("/api/graphql", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            query: `
              mutation UpdateUserProfile($name: String!, $fullSizeScreen: Boolean) {
                updateUserProfile(name: $name, fullSizeScreen: $fullSizeScreen) {
                  id
                  fullSizeScreen
                }
              }
            `,
            variables: {
              name: user.name,
              fullSizeScreen: true
            }
          })
        }).catch(() => {})
      }
    }
    try {
      sessionStorage.setItem("bold_fullscreen_dismissed_session", "true")
    } catch (e) {}
  }

  const handleDismiss = () => {
    persistPreference()
    onClose()
  }

  const handleAccept = () => {
    persistPreference()
    onEnableFullscreen()
  }

  return (
    <div className="fixed inset-0 z-[99999] bg-black/65 backdrop-blur-md flex items-center justify-center p-4 font-sans animate-in fade-in duration-300 select-none">
      <div className="relative w-full max-w-lg bg-white dark:bg-zinc-900 border border-zinc-200/80 dark:border-zinc-800 rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Subtle decorative top gradient */}
        <div className="h-1.5 w-full bg-gradient-to-r from-[#004EB1] via-blue-500 to-indigo-600" />

        {/* Close icon button */}
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 p-1.5 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors cursor-pointer"
          aria-label="Fechar"
        >
          <X className="size-4" />
        </button>

        <div className="p-6 sm:p-8 space-y-6">
          {/* Header com Ícone e Badge */}
          <div className="flex items-start gap-4">
            <div className="size-13 rounded-2xl bg-[#004EB1]/10 dark:bg-blue-950/40 border border-[#004EB1]/20 flex items-center justify-center text-[#004EB1] dark:text-blue-400 shrink-0 shadow-inner">
              <Maximize2 className="size-6.5" />
            </div>

            <div className="space-y-1">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-extrabold tracking-wide uppercase bg-blue-50 dark:bg-blue-950/30 text-[#004EB1] dark:text-blue-400 border border-blue-200/60 dark:border-blue-900/40">
                <Sparkles className="size-3" />
                Experiência Recomendada
              </div>
              <h3 className="text-base sm:text-lg font-bold text-zinc-900 dark:text-white leading-tight">
                Otimizar Visualização do Workspace
              </h3>
            </div>
          </div>

          {/* Texto explicativo formal */}
          <p className="text-xs sm:text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">
            Para proporcionar uma experiência completa, fluida e imersiva de sistema operacional desktop corporativo, recomendamos a utilização do <strong>Bold Workspace</strong> em <strong>modo de tela cheia</strong>.
          </p>

          {/* Destaques de Benefícios */}
          <div className="bg-zinc-50 dark:bg-zinc-950/60 rounded-2xl border border-zinc-150 dark:border-zinc-800/80 p-4 space-y-2.5 text-xs text-zinc-700 dark:text-zinc-300">
            <div className="flex items-center gap-2.5">
              <div className="size-5 rounded-md bg-blue-500/10 dark:bg-blue-500/20 text-[#004EB1] dark:text-blue-400 flex items-center justify-center shrink-0">
                <Monitor className="size-3.5" />
              </div>
              <span><strong>Área Útil Expandida:</strong> Aproveitamento de 100% da resolução do seu monitor.</span>
            </div>
            <div className="flex items-center gap-2.5">
              <div className="size-5 rounded-md bg-blue-500/10 dark:bg-blue-500/20 text-[#004EB1] dark:text-blue-400 flex items-center justify-center shrink-0">
                <Sparkles className="size-3.5" />
              </div>
              <span><strong>Foco & Produtividade:</strong> Oculta barras do navegador para evitar distrações.</span>
            </div>
          </div>

          {/* Opção de não lembrar */}
          <label className="flex items-center gap-2.5 text-xs text-zinc-500 dark:text-zinc-400 cursor-pointer hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="rounded border-zinc-300 dark:border-zinc-700 text-[#004EB1] focus:ring-[#004EB1] size-3.5 cursor-pointer"
            />
            <span>Não exibir esta sugestão novamente neste dispositivo</span>
          </label>

          {/* Ações */}
          <div className="flex flex-col-reverse sm:flex-row items-center justify-end gap-2.5 pt-2 border-t border-zinc-100 dark:border-zinc-800/80">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              className="w-full sm:w-auto text-xs h-9 px-4 font-semibold text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white cursor-pointer"
            >
              Continuar em Janela
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleAccept}
              className="w-full sm:w-auto bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs h-9 px-5 font-bold shadow-md shadow-[#004EB1]/20 cursor-pointer flex items-center justify-center gap-2"
            >
              <Maximize2 className="size-3.5" />
              <span>Habilitar Tela Cheia</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
